import { HttpClient } from '@/shared/infra/http/HttpClient';
import { AuditLogRepository } from '../../application/ports/AuditLogRepository';
import { AuditLogResult } from '../../application/domain/entities/AuditLogResult';
import { ComposedAuditTrailDto } from '../dto/ComposedAuditTrailDto';
import { ComposedAuditTrailMapper } from '../dto/ComposedAuditTrailMapper';

/**
 * The Audit Log reads the SAME composed trail the Audit Report does.
 *
 * It previously read `/i9Form/:id/audit-report` — one source — while the Report composed three. That is
 * why the two screens showed different data: they were different compositions, so no amount of display
 * work could have reconciled them. Both now derive from one response.
 *
 * Paginated here rather than server-side. The trail is one record's history — tens of events, not
 * thousands — and slicing locally is what lets this screen stay paginated while guaranteeing it holds
 * exactly the events the Report shows. A server page size would reintroduce the chance of divergence.
 */
export class ApiAuditLogRepository implements AuditLogRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async fetchAuditLogs(i9FormId: string, page: number, pageSize: number): Promise<AuditLogResult> {
    const response = await this.httpClient.get<ComposedAuditTrailDto>(
      `/i9Form/${i9FormId}/audit-trail`,
    );
    // Newest first — a log is read from the top, unlike the Report's chronological narrative.
    const all = ComposedAuditTrailMapper.toLogItems(response.data).reverse();

    const safePageSize = pageSize > 0 ? pageSize : 10;
    const totalPages = Math.max(1, Math.ceil(all.length / safePageSize));
    // Clamp so a stale page number from a previous, longer trail can't render an empty screen.
    const currentPage = Math.min(Math.max(1, page), totalPages);
    const start = (currentPage - 1) * safePageSize;

    return {
      items: all.slice(start, start + safePageSize),
      currentPage,
      itemsPerPage: safePageSize,
      totalItems: all.length,
      totalPages,
    };
  }
}
